import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import AnalystBroNavbar from "../components/AnalystBroNavbar";
import AnalystBroFooter from "../components/AnalystBroFooter";
import { ArrowRight, Clock, Droplets, Ban, Gauge, Receipt, Monitor } from "lucide-react";

// Public page for the desktop backtesting engine. Keep every claim in step with the engine:
// analystbro-desktop src-tauri/py/abbt.py + abengine.py and src-tauri/src/mcp/backtest.rs.
// Per-market rules files (docs/045) are a plan: describe them as coming, not shipped.
const THEME = "ab-light";

const eyebrow = "font-mono text-xs uppercase tracking-[0.12em] text-[var(--ab-accent)]";
const h2 = "font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1]";
const muted = "text-[var(--ab-muted)] leading-relaxed";
const card = "rounded-2xl border border-[var(--ab-border)] p-6 flex flex-col gap-3";
const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--ab-accent)] px-6 min-h-[48px] font-semibold text-[var(--ab-on-accent)] hover:bg-[var(--ab-accent-hover)] transition";

const FRICTIONS = [
  {
    icon: Clock,
    title: "Settlement per stock and per date",
    body: "Bought shares can't be sold until they arrive. Settlement days can be set per symbol and per date range: on the Dhaka Stock Exchange, for example, T+2 normally and T+3 for Z-category stocks. Sales that have to wait are counted in the report. Sale proceeds can also be held until they settle.",
  },
  {
    icon: Droplets,
    title: "A cap on volume",
    body: "Each fill can be limited to a share of the bar's traded volume. The rest of the order carries to later bars, so a thin stock can't be bought or sold at sizes the market never traded. The report shows how much of each order actually filled.",
  },
  {
    icon: Ban,
    title: "No short selling where there is none",
    body: "Turn shorting off for long-only markets. A sell that would take a position below zero is refused and counted.",
  },
  {
    icon: Gauge,
    title: "Price limits and interventions",
    body: "With a daily price limit set, a buy can't fill on a bar locked at the upper limit, or a sell at the lower one. Periods such as floor prices show up in the real bars and the volume cap, and can be left out of an extra set of results.",
  },
  {
    icon: Receipt,
    title: "Costs per market",
    body: "Commission and slippage per run, and sweeps over several cost levels in one go. Costs on some exchanges are many times what US-built defaults assume.",
  },
];

const ENGINE = [
  ["Pine Script-style strategies", "strategy.entry, strategy.exit and strategy.close, partial exits with qty_percent, and sizing by fixed shares, cash or percent of equity, named as in TradingView's Pine Script."],
  ["Checked against TradingView", "On our test strategies (for example on QQQ and GLD) we compared runs trade by trade with TradingView's Strategy Tester. Known differences remain: TradingView can fill a stop on the bar the trade entered, for instance."],
  ["Fast whole-history plans", "For most research a strategy can describe its signals for the whole history at once, which runs several times faster and gives the same trades."],
  ["Prices from your TradingView", "Daily and intraday bars come through your own TradingView app. How far back intraday data goes depends on your TradingView plan."],
];

export default function BacktestingPage() {
  useEffect(() => {
    document.title = "Backtesting for markets with more frictions | AnalystBro";
  }, []);

  return (
    <div className={`${THEME} min-h-screen flex flex-col font-sans antialiased`}>
      <AnalystBroNavbar theme={THEME} />

      <main className="flex-1">
        <section className="max-w-4xl mx-auto px-4 sm:px-8 pt-16 md:pt-28 pb-12 flex flex-col gap-6">
          <span className={eyebrow}>Desktop app · Backtesting</span>
          <h1 className="font-display text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05]">
            Backtesting for markets with more frictions
          </h1>
          <p className={`${muted} text-lg max-w-3xl`}>
            Many backtesters assume instant fills, the same settlement for every stock, short selling and deep
            liquidity. Plenty of markets work differently: slower settlement that depends on a stock’s category,
            thin trading, no shorting, price limits and interventions. AnalystBro’s backtesting engine is built
            to model those rules, so a test reflects what you could actually have traded.
          </p>
          <p className={`${muted} max-w-3xl`}>
            It runs locally in the AnalystBro desktop app on Windows and macOS. Your AI agent, on the Claude,
            Codex or Antigravity plan you already have, writes the strategy and runs it there.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <a href="/#download" className={btnPrimary}>
              Get the desktop app <ArrowRight size={18} />
            </a>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12 md:py-20 flex flex-col gap-10">
          <div className="flex flex-col gap-3 max-w-2xl">
            <span className={eyebrow}>Market rules</span>
            <h2 className={h2}>The frictions it models</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FRICTIONS.map(({ icon: Icon, title, body }) => (
              <div key={title} className={card}>
                <Icon size={22} className="text-[var(--ab-accent)]" />
                <h3 className="font-bold text-lg">{title}</h3>
                <p className={muted}>{body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-8 py-12 md:py-20 flex flex-col gap-5">
          <span className={eyebrow}>Why it matters</span>
          <h2 className={h2}>The rules can make most of the number</h2>
          <p className={muted}>
            In our own research, a breakout strategy on Dhaka Stock Exchange stocks showed about +676% from
            February 2024 to October 2026 in a naive backtest. With fills capped at a share of each day’s volume
            and a minimum-turnover filter, the same idea came to roughly +24% a year. The strategy didn’t
            change; the assumptions about what could be traded did.
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12 md:py-20 flex flex-col gap-10">
          <div className="flex flex-col gap-3 max-w-2xl">
            <span className={eyebrow}>The engine</span>
            <h2 className={h2}>Familiar to write, checked against TradingView</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {ENGINE.map(([t, b]) => (
              <div key={t} className={card}>
                <h3 className="font-bold text-lg">{t}</h3>
                <p className={muted}>{b}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-8 py-12 md:py-20 flex flex-col gap-5">
          <span className={eyebrow}>Markets</span>
          <h2 className={h2}>Built with these markets in mind</h2>
          <p className={muted}>
            Bangladesh (DSE and CSE), Pakistan, Sri Lanka, Nepal, India, Vietnam, Indonesia and Egypt, among
            others. Today you or your agent set the rules for each run. Maintained rules files for each market,
            with dated settlement, price-limit and intervention history, are planned, starting with Bangladesh.
            They don’t cover every rule of every market yet.
          </p>
          <div className="flex items-center gap-3 text-[var(--ab-muted)]">
            <Monitor size={18} /> <span>Windows and macOS</span>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-8 pb-20 md:pb-28 flex flex-col gap-6">
          <p className="text-sm text-[var(--ab-muted)] leading-relaxed border-t border-[var(--ab-border)] pt-6">
            Backtests show how a set of rules would have behaved on past data under the assumptions you give
            them. They are not predictions, and past results don’t indicate future returns. Nothing here is
            investment advice.
          </p>
          <div>
            <Link to="/" className="font-semibold text-[var(--ab-accent)] hover:underline">
              ← Back to AnalystBro
            </Link>
          </div>
        </section>
      </main>

      <AnalystBroFooter theme={THEME} />
    </div>
  );
}
